import React, { useState, useEffect } from 'react';
import { Spin, Empty, Card, Row, Col, Typography, Button, Drawer } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import CustomList from '../../components/List';
import ListItem from '../../components/ListItem';
import CustomPagination from '../../components/CustomPagination';
import API from '../../../API';

const { Text, Title } = Typography;

const ProjectSubComponentsTab = ({ projectId }) => {
    const [subComponents, setSubComponents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedSubComponentId, setSelectedSubComponentId] = useState(null);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [selectedSubComponent, setSelectedSubComponent] = useState(null);
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 10,
        total: 0,
        last_page: 1
    });
    const [currentPage, setCurrentPage] = useState(1);

    const fetchSubComponents = async (page = 1, pageSize = pagination.per_page) => {
        if (!projectId) return;
        
        setLoading(true);
        try {
            const response = await API.getProjectSubComponent(
                { 'filter[project_id]': projectId }, 
                page, 
                pageSize
            );
            
            let subComponentsData = [];
            let paginationData = pagination;
            
            if (response && response?.data?.data) {
                subComponentsData = response.data.data;
            } else if (Array.isArray(response?.data)) {
                subComponentsData = response?.data;
            }
            
            if (response && response?.data?.pagination) {
                paginationData = response?.data?.pagination;
            }
            
            setSubComponents(subComponentsData);
            setPagination(paginationData);
        } catch (error) {
            console.error('Error fetching sub components:', error);
            setSubComponents([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSubComponents();
    }, [projectId]);

    const handleSubComponentsPageChange = (page, pageSize) => {
        setCurrentPage(page);
        fetchSubComponents(page, pageSize);
    };

    const handleViewDetails = (subComponent) => {
        setSelectedSubComponent(subComponent);
        setDrawerVisible(true);
    };

    const handleCloseDrawer = () => {
        setDrawerVisible(false);
        setSelectedSubComponent(null);
    };


    if (loading && subComponents.length === 0) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!loading && subComponents.length === 0) {
        return (
            <Empty 
                description="No sub components found for this project"
                style={{ margin: '50px 0' }}
            />
        );
    }

    return (
        <Card>
            <Row gutter={[16, 16]}>
                <Col xs={24} lg={24}>
                    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Title level={4} style={{ margin: 0 }}>Project Sub-Components</Title>
                    </div>
                    <div>
                        {loading ? (
                            <Spin />
                        ) : (
                            <>
                                <CustomList
                                    itemName="Sub Components"
                                    items={subComponents}
                                    page={currentPage}
                                    itemCount={pagination?.total || (subComponents?.length || 0)}
                                    loading={loading}
                                    headerLayout={[
                                        { xxl: 7, xl: 7, lg: 7, md: 7, sm: 24, xs: 24, header: 'Name' },
                                        { xxl: 12, xl: 12, lg: 12, md: 12, sm: 24, xs: 24, header: 'Description' },
                                        { xxl: 4, xl: 4, lg: 4, md: 4, sm: 24, xs: 24, header: 'Actions' }
                                    ]}
                                    renderListItem={({ item }) => (
                                        <ListItem
                                            key={item.id}
                                            name={item.name}
                                            item={item}
                                            isPackage={false}
                                            className={selectedSubComponentId === item.id ? 'selected' : ''}
                                            onClick={() => setSelectedSubComponentId(item.id)}
                                        >
                                            <Row gutter={[8, 0]} style={{ width: '100%' }}>
                                                <Col xxl={7} xl={7} lg={7} md={7} sm={24} xs={24} className="contentEllipse">
                                                    <div className="mobile-label">Name</div>
                                                    <Text strong>{item.name || '-'}</Text>
                                                </Col>
                                                <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24} className="contentEllipse">
                                                    <div className="mobile-label">Description</div>
                                                    <Text>{item.description || '-'}</Text>
                                                </Col>
                                                <Col xxl={4} xl={4} lg={4} md={4} sm={24} xs={24} className="contentEllipse">
                                                    <div className="mobile-label">Actions</div>
                                                    <div style={{ width: '100%', display: 'flex', gap: '8px', justifyContent: 'flex-start', alignItems: 'center', position: 'relative', zIndex: 10 }}>
                                                        <Button
                                                            type="secondary"
                                                            icon={<EyeOutlined />}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleViewDetails(item);
                                                            }}
                                                            size="small"
                                                            title="View Details"
                                                            style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                                                        >
                                                            View
                                                        </Button>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </ListItem>
                                    )}
                                    showToolbar={false}
                                />
                                
                                {pagination && pagination.total > 0 && (
                                    <CustomPagination 
                                        pagination={pagination}
                                        onPageChange={handleSubComponentsPageChange}
                                        loading={loading}
                                    />
                                )}
                            </>
                        )}
                    </div>
                </Col>
            </Row>

            <Drawer
                title="Sub-Component Details"
                width={500}
                onClose={handleCloseDrawer}
                open={drawerVisible}
                bodyStyle={{ paddingBottom: 80 }}
            >
                {selectedSubComponent && (
                    <div style={{ padding: '16px 0' }}>
                        <div style={{ marginBottom: '16px' }}>
                            <Text strong>Name: </Text>
                            <Text>{selectedSubComponent.name || '-'}</Text>
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                            <Text strong>Description: </Text>
                            <Text>{selectedSubComponent.description || '-'}</Text>
                        </div>
                    </div>
                )}
            </Drawer>
        </Card>
    );
};

export default ProjectSubComponentsTab;
